import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import TestRequestFormWithCountries from "./TestRequestFormWithCountries";


const PricingSection = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);

  const offers = [
    {
      name: "TEST",
      duration: "24H - ESSAI",
      price: "GRATUIT",
      pricePerMonth: null,
      popular: false,
      isTest: true,
      cta: "ESSAYEZ GRATUITEMENT",
      ctaLink: "",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT 7J/7", included: true },
      ],
    },
    {
      name: "3 MOIS",
      duration: "90 JOURS - ABONNEMENT",
      price: "25",
      pricePerMonth: "8,33",
      popular: false,
      isTest: false,
      stripeProductType: "3months" as const,
      cta: "PAYEZ MAINTENANT",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT 7J/7", included: true },
      ],
    },
    {
      name: "6 MOIS",
      duration: "180 JOURS - ABONNEMENT",
      price: "40",
      pricePerMonth: "6,67",
      popular: false,
      isTest: false,
      stripeProductType: "6months" as const,
      cta: "PAYEZ MAINTENANT",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT 7J/7", included: true },
      ],
    },
    {
      name: "12 MOIS",
      duration: "365 JOURS",
      price: "60",
      pricePerMonth: "5",
      popular: true,
      isTest: false,
      stripeProductType: "12months" as const,
      cta: "PAYEZ MAINTENANT",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT VIP 24/7", included: true },
      ],
    },
    {
      name: "MENSUEL",
      duration: "ACCÈS MENSUEL",
      price: "9,99",
      pricePerMonth: "9,99",
      popular: false,
      isTest: false,
      isMonthly: true,
      stripeProductType: "monthly" as const,
      cta: "OBTENIR MON ACCÈS",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT 7J/7", included: true },
      ],
    },
  ];

  // Auto-scroll effect on mobile when section comes into view
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || hasAutoScrolled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAutoScrolled) {
            setHasAutoScrolled(true);
            
            // Wait a bit then auto-scroll to show there are more cards
            setTimeout(() => {
              const cardWidth = 280 + 16; // card width + gap
              container.scrollTo({
                left: cardWidth * 1.5, // Scroll to show part of 2nd and 3rd card
                behavior: "smooth",
              });
              
              // Scroll back after a moment
              setTimeout(() => {
                container.scrollTo({
                  left: 0,
                  behavior: "smooth",
                });
              }, 800);
            }, 500);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, [hasAutoScrolled]);

  return (
    <section id="pricing" className="py-12 md:py-20 px-4 bg-background">
      <div className="container mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-3 md:mb-4 text-foreground tracking-wide">
            NOS OFFRES D'ABONNEMENT
          </h2>
          <p className="text-sm md:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
            Paiement sécurisé par Stripe. Vos identifiants seront envoyés par email après le paiement.
          </p>
        </div>

        {/* Mobile: horizontal scroll */}
        <div className="md:hidden relative">
          <div 
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide py-2 px-2 -mx-2"
          >
            {/* Mobile order: Test, Mensuel, 3m, 6m, 12m */}
            {[offers[0], offers[4], offers[1], offers[2], offers[3]].map((offer, index) => (
              <Card
                key={index}
                className={`relative overflow-hidden transition-all duration-300 rounded-xl border flex flex-col snap-center flex-shrink-0 w-[280px] ${
                  offer.popular
                    ? "bg-gradient-to-b from-primary to-primary/80 border-primary/50"
                    : "bg-card border-primary/20 hover:border-primary/40"
                }`}
              >
                <CardContent className="p-5 text-center flex flex-col flex-1">
                  <h3 className={`text-xl font-bold mb-1 tracking-wider ${offer.popular ? "text-primary-foreground" : "text-primary"}`}>
                    {offer.name}
                  </h3>
                  <p className={`text-xs mb-3 tracking-wide ${offer.popular ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {offer.duration}
                  </p>
                  <div className="mb-4 min-h-[70px]">
                    <div className={`text-4xl font-black ${offer.popular ? "text-primary-foreground" : "text-foreground"}`}>
                      {offer.isTest ? offer.price : `${offer.price} €`}
                    </div>
                    {offer.pricePerMonth ? (
                      <p className={`text-xs mt-1 ${offer.popular ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        soit {offer.pricePerMonth} €/mois
                      </p>
                    ) : (
                      <p className="text-sm mt-1 invisible">-</p>
                    )}
                  </div>
                  {offer.isTest ? (
                    <div className="w-full mb-4">
                      <TestRequestFormWithCountries
                        trigger={
                          <Button className="w-full font-bold py-4 text-xs rounded-full transition-all duration-300 group btn-primary text-primary-foreground">
                            {offer.cta}
                            <ArrowRight className="ml-2 h-3 w-3 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        }
                      />
                    </div>
                  ) : null}
                  <ul className="space-y-2 text-left mb-4 flex-1">
                    {offer.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-secondary flex-shrink-0">✅</span>
                        <span className={`text-xs ${offer.popular ? "text-primary-foreground/90" : "text-foreground/80"}`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className={`pt-3 border-t text-left text-[10px] ${offer.popular ? "border-primary-foreground/20" : "border-border"}`}>
                    <p className={offer.popular ? "text-primary-foreground/80" : "text-muted-foreground"}>
                      📚 Tutoriels fournis par email + disponibles sur{" "}
                      <Link to="/tutoriels" className={`underline hover:no-underline ${offer.popular ? "text-black font-semibold" : "text-primary"}`}>
                        la page Tutoriels
                      </Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-4">
            ← Glissez pour voir toutes les offres →
          </p>
        </div>

        {/* Desktop: 4 cards (Test, 3m, 6m, 12m) + monthly banner below */}
        <div className="hidden md:block space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {offers.filter(o => !o.isMonthly).map((offer, index) => (
              <Card
                key={index}
                className={`relative overflow-hidden transition-all duration-300 hover:scale-105 rounded-xl border flex flex-col ${
                  offer.popular
                    ? "bg-gradient-to-b from-primary to-primary/80 border-primary/50 glow-primary"
                    : "bg-card border-primary/20 hover:border-primary/40"
                }`}
              >
                <CardContent className="p-6 text-center flex flex-col flex-1">
                  <h3 className={`text-2xl font-bold mb-2 tracking-wider ${offer.popular ? "text-primary-foreground" : "text-primary"}`}>
                    {offer.name}
                  </h3>
                  <p className={`text-sm mb-4 tracking-wide ${offer.popular ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {offer.duration}
                  </p>
                  <div className="mb-4 min-h-[80px]">
                    <div className={`text-5xl font-black ${offer.popular ? "text-primary-foreground" : "text-foreground"}`}>
                      {offer.isTest ? offer.price : `${offer.price} €`}
                    </div>
                    {offer.pricePerMonth ? (
                      <p className={`text-sm mt-1 ${offer.popular ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        soit {offer.pricePerMonth} €/mois
                      </p>
                    ) : (
                      <p className="text-sm mt-1 invisible">-</p>
                    )}
                  </div>
                  {offer.isTest ? (
                    <div className="w-full mb-6">
                      <TestRequestFormWithCountries
                        trigger={
                          <Button className="w-full font-bold py-5 text-sm rounded-full transition-all duration-300 group btn-primary text-primary-foreground">
                            {offer.cta}
                            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        }
                      />
                    </div>
                  ) : null}
                  <ul className="space-y-3 text-left mb-4 flex-1">
                    {offer.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-secondary flex-shrink-0">✅</span>
                        <span className={`text-sm ${offer.popular ? "text-primary-foreground/90" : "text-foreground/80"}`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className={`pt-4 border-t text-left text-xs ${offer.popular ? "border-primary-foreground/20" : "border-border"}`}>
                    <p className={offer.popular ? "text-primary-foreground/80" : "text-muted-foreground"}>
                      📚 Tutoriels fournis par email + disponibles sur{" "}
                      <Link to="/tutoriels" className={`underline hover:no-underline ${offer.popular ? "text-black font-semibold" : "text-primary"}`}>
                        la page Tutoriels
                      </Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Monthly banner */}
          {(() => {
            const monthlyOffer = offers.find(o => o.isMonthly);
            if (!monthlyOffer) return null;
            return (
              <Card className="relative overflow-hidden transition-all duration-300 hover:scale-[1.02] rounded-xl border-2 bg-card border-primary/40 hover:border-primary/80">
                <CardContent className="p-6 lg:p-8">
                  <div className="flex items-center gap-6 lg:gap-8">
                    <div className="text-center shrink-0">
                      <h3 className="text-xl lg:text-2xl font-bold tracking-wider text-primary">{monthlyOffer.name}</h3>
                      <p className="text-xs lg:text-sm tracking-wide text-muted-foreground mt-1">{monthlyOffer.duration}</p>
                    </div>
                    <div className="text-center shrink-0">
                      <div className="text-3xl lg:text-5xl font-black text-foreground">{monthlyOffer.price} €</div>
                      <p className="text-xs lg:text-sm mt-1 text-muted-foreground">soit {monthlyOffer.pricePerMonth} €/mois</p>
                    </div>
                    <ul className="flex-1 grid grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1.5">
                      {monthlyOffer.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-1.5">
                          <span className="text-secondary flex-shrink-0 text-sm">✅</span>
                          <span className="text-xs lg:text-sm text-foreground/80">{feature.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-center text-sm text-primary font-semibold mt-4">
                    💰 Économisez plus de 800 € par an par rapport aux plateformes classiques !
                  </p>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;